import React, { useEffect, useState } from 'react'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@/components/ui'
import {
  getMetrics,
  listTicketSyncs,
  getTicketSync,
  retryTicketSync,
  listMessageSyncs,
  retryMessageSync,
  listUserMappings,
  refreshUserMapping,
  listSubscriptions,
  debugDept,
  listDebugUsers,
  diagnoseChat,
  type BridgeMetrics,
  type TicketSync,
  type TicketSyncDetail,
  type MessageSync,
  type AuditEntry,
  type UserMapping,
  type BridgeSubscription,
  type DeptDebugResult,
  type DebugUser,
  type DiagnoseChatResult,
} from '@/lib/api/tanssTeamsBridge'

// ─── TANSS ↔ Microsoft-Teams-Bridge Admin-Page ──────────────────────────────
//
// Read-only Status + manuelle Retry-Aktionen. Der eigentliche Sync laeuft
// als Scheduled-Job (siehe Settings → Scheduled Jobs:
// `tanss-teams-bridge.poll`, `.subscriptions-renew`, `.retry-failed`).
//
// Doku: docs/architecture/tanss-teams-bridge.md

export default function TanssTeamsBridgePage() {
  const { toast: _toast } = useToast()
  const [metrics, setMetrics] = useState<BridgeMetrics | null>(null)
  const [metricsErr, setMetricsErr] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void getMetrics()
      .then((m) => !cancelled && setMetrics(m))
      .catch((e: unknown) => !cancelled && setMetricsErr(extractErrorDetails(e)))
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="space-y-6">
      <PageHeader
        title="TANSS ↔ Teams Bridge"
        breadcrumbs={[{ label: 'Admin' }, { label: 'TANSS↔Teams' }]}
        description="Status der ticketbezogenen TANSS-Chat-Spiegelung in Microsoft Teams. Polling, Renewal und Retry laufen als Scheduled Jobs (Settings → Scheduled Jobs)."
      />

      {metricsErr && <ErrorState title="Metriken konnten nicht geladen werden." details={metricsErr} />}
      {metrics && <MetricsPanel metrics={metrics} />}

      <Tabs defaultValue="ticket-syncs">
        <TabsList>
          <TabsTrigger value="ticket-syncs">Ticket-Syncs</TabsTrigger>
          <TabsTrigger value="messages">Nachrichten</TabsTrigger>
          <TabsTrigger value="users">User-Mapping</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="debug-dept">Debug: Abteilung</TabsTrigger>
          <TabsTrigger value="debug-users">Debug: User</TabsTrigger>
          <TabsTrigger value="diagnose-chat">Chat-Diagnose</TabsTrigger>
        </TabsList>
        <TabsContent value="ticket-syncs"><TicketSyncsTab /></TabsContent>
        <TabsContent value="messages"><MessagesTab /></TabsContent>
        <TabsContent value="users"><UserMappingsTab /></TabsContent>
        <TabsContent value="subscriptions"><SubscriptionsTab /></TabsContent>
        <TabsContent value="debug-dept"><DebugDeptTab /></TabsContent>
        <TabsContent value="debug-users"><DebugUsersTab /></TabsContent>
        <TabsContent value="diagnose-chat"><DiagnoseChatTab /></TabsContent>
      </Tabs>

      <Card>
        <Card.Header>
          <h2 className="text-h3">Hinweis: Polling-Frequenz</h2>
        </Card.Header>
        <Card.Body>
          <p className="text-sm text-body">
            Der Sync wird durch die Scheduled-Jobs <code className="font-mono text-xs">tanss-teams-bridge.poll</code>,
            {' '}<code className="font-mono text-xs">tanss-teams-bridge.subscriptions-renew</code> und
            {' '}<code className="font-mono text-xs">tanss-teams-bridge.retry-failed</code> getrieben.
            Aktivieren / deaktivieren / Intervalle aendern in <strong>Settings → Scheduled Jobs</strong>.
            Die Jobs starten im Repo bewusst deaktiviert — erst nach kompletter Konfiguration
            der ENV-Variablen (M365_*, M365_TEAMS_SERVICE_USER_ID, M365_GRAPH_WEBHOOK_BASE_URL,
            M365_GRAPH_WEBHOOK_CLIENT_STATE_SECRET) anschalten.
          </p>
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── Metrics-Panel ──────────────────────────────────────────────────────────

function MetricsPanel({ metrics }: { metrics: BridgeMetrics }) {
  const cells: { label: string; value: string | number; tone?: 'ok' | 'warn' | 'err' }[] = [
    { label: 'Aktive Ticket-Syncs', value: metrics.activeTicketSyncs, tone: 'ok' },
    { label: 'Fehlgeschlagene Ticket-Syncs', value: metrics.failedTicketSyncs, tone: metrics.failedTicketSyncs > 0 ? 'err' : 'ok' },
    { label: 'Pending Nachrichten', value: metrics.pendingMessageSyncs, tone: metrics.pendingMessageSyncs > 0 ? 'warn' : 'ok' },
    { label: 'Fehlgeschlagene Nachrichten', value: metrics.failedMessageSyncs, tone: metrics.failedMessageSyncs > 0 ? 'err' : 'ok' },
    { label: 'Abgelaufene Subscriptions', value: metrics.expiredSubscriptions, tone: metrics.expiredSubscriptions > 0 ? 'err' : 'ok' },
    { label: 'Letzter Sync', value: metrics.lastPollAt ? new Date(metrics.lastPollAt).toLocaleString('de-DE') : '–' },
  ]
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {cells.map((c) => (
        <Card key={c.label}>
          <Card.Body className="space-y-1">
            <div className="text-xs text-muted">{c.label}</div>
            <div
              className={
                c.tone === 'err'
                  ? 'text-h2 text-danger'
                  : c.tone === 'warn'
                  ? 'text-h2 text-warning'
                  : 'text-h2'
              }
            >
              {c.value}
            </div>
          </Card.Body>
        </Card>
      ))}
    </div>
  )
}

// ─── Tab: Ticket-Syncs ──────────────────────────────────────────────────────

function TicketSyncsTab() {
  const { toast } = useToast()
  const [items, setItems] = useState<TicketSync[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)

  async function load() {
    setError(null)
    try {
      setItems(await listTicketSyncs({ limit: 200 }))
    } catch (e) {
      setError(extractErrorDetails(e))
    }
  }
  useEffect(() => { void load() }, [])

  async function onRetry(id: string) {
    try {
      await retryTicketSync(id)
      toast({ tone: 'success', title: 'Retry ausgeloest.' })
      void load()
    } catch (e) {
      toast({ tone: 'danger', title: extractErrorDetails(e) })
    }
  }

  if (error) return <ErrorState title="Ticket-Syncs konnten nicht geladen werden." details={error} />
  if (!items) return <LoadingState />
  if (items.length === 0) {
    return (
      <EmptyState
        title="Noch keine Ticket-Syncs"
        description="Sobald der Job tanss-teams-bridge.poll laeuft und ticketbezogene TANSS-Chats findet, erscheinen sie hier."
      />
    )
  }

  return (
    <>
      <DataTable<TicketSync>
        data={items}
        getRowId={(r) => r.id}
        columns={[
          {
            id: 'tanss_ticket',
            header: 'Ticket',
            cell: ({ row }) =>
              row.original.ticket_url ? (
                <a className="text-link" href={row.original.ticket_url} target="_blank" rel="noopener noreferrer">
                  #{row.original.tanss_ticket_id}
                </a>
              ) : (
                <span>#{row.original.tanss_ticket_id}</span>
              ),
          },
          { accessorKey: 'teams_topic', header: 'Topic' },
          {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }) => <StatusBadge status={row.original.status} />,
          },
          {
            id: 'chat_status',
            header: 'Chat',
            cell: ({ row }) => (
              <StatusBadge
                status={row.original.chat_status === 'closed' ? 'closed' : 'open'}
              />
            ),
          },
          {
            accessorKey: 'last_sync_at',
            header: 'Letzter Sync',
            cell: ({ row }) =>
              row.original.last_sync_at
                ? new Date(row.original.last_sync_at).toLocaleString('de-DE')
                : '–',
          },
          {
            accessorKey: 'last_error',
            header: 'Letzter Fehler',
            cell: ({ row }) =>
              row.original.last_error ? (
                <span className="text-xs text-danger" title={row.original.last_error}>
                  {row.original.last_error.slice(0, 60)}
                  {row.original.last_error.length > 60 ? '…' : ''}
                </span>
              ) : (
                '–'
              ),
          },
          {
            id: 'actions',
            header: 'Aktionen',
            enableSorting: false,
            cell: ({ row }) => (
              <div className="flex gap-1">
                <Button variant="ghost" size="xs" onClick={() => setDetailId(row.original.id)}>Details</Button>
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={() => void onRetry(row.original.id)}
                  disabled={row.original.status === 'synced' || row.original.status === 'ignored'}
                >
                  Retry
                </Button>
              </div>
            ),
          },
        ]}
      />
      {detailId && <DetailModal id={detailId} onClose={() => setDetailId(null)} />}
    </>
  )
}

// ─── Detail-Modal mit Audit-Trail ──────────────────────────────────────────

function DetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const [detail, setDetail] = useState<TicketSyncDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    void getTicketSync(id)
      .then((d) => !cancelled && setDetail(d))
      .catch((e: unknown) => !cancelled && setError(extractErrorDetails(e)))
    return () => {
      cancelled = true
    }
  }, [id])

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`Ticket-Sync ${id.slice(0, 8)}`} size="lg">
      {error && <ErrorState title="Details konnten nicht geladen werden." details={error} />}
      {!detail && !error && <LoadingState />}
      {detail && (
        <div className="space-y-4">
          <Card>
            <Card.Header><h3 className="text-h4">TicketSync</h3></Card.Header>
            <Card.Body>
              <DetailFacts ticketSync={detail.ticketSync} />
            </Card.Body>
          </Card>
          <Card>
            <Card.Header><h3 className="text-h4">Nachrichten ({detail.messages.length})</h3></Card.Header>
            <Card.Body className="p-0">
              {detail.messages.length === 0 ? (
                <div className="px-4 py-3 text-sm text-muted">Keine Nachrichten gespiegelt.</div>
              ) : (
                <MessageSyncTable messages={detail.messages} />
              )}
            </Card.Body>
          </Card>
          <Card>
            <Card.Header><h3 className="text-h4">Audit-Trail ({detail.audit.length})</h3></Card.Header>
            <Card.Body className="p-0">
              {detail.audit.length === 0 ? (
                <div className="px-4 py-3 text-sm text-muted">Noch kein Audit-Eintrag.</div>
              ) : (
                <AuditTable entries={detail.audit} />
              )}
            </Card.Body>
          </Card>
        </div>
      )}
    </Modal>
  )
}

function DetailFacts({ ticketSync }: { ticketSync: TicketSync }) {
  const rows: Array<[string, React.ReactNode]> = [
    ['Status', <StatusBadge key="s" status={ticketSync.status} />],
    [
      'Chat-Status',
      <StatusBadge
        key="cs"
        status={ticketSync.chat_status === 'closed' ? 'closed' : 'open'}
      />,
    ],
    ['Letzte Status-Aenderung', ticketSync.last_status_change_at ?? '–'],
    ['TANSS-Ticket-ID', ticketSync.tanss_ticket_id ?? '–'],
    ['TANSS-Chat-ID', ticketSync.tanss_chat_id],
    ['Teams-Chat-ID', ticketSync.teams_chat_id ?? '–'],
    ['Topic', ticketSync.teams_topic ?? '–'],
    ['Letzter Sync', ticketSync.last_sync_at ?? '–'],
    ['Letzter Fehler', ticketSync.last_error ? (
      <span className="text-danger break-words">{ticketSync.last_error}</span>
    ) : '–'],
    ['Angelegt', ticketSync.created_at],
    ['Aktualisiert', ticketSync.updated_at],
  ]
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt className="text-muted">{label}</dt>
          <dd className="break-words">{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  )
}

function MessageSyncTable({ messages }: { messages: MessageSync[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-surface-alt text-left">
          <tr>
            <th className="px-3 py-2">Origin</th>
            <th className="px-3 py-2">Richtung</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">TANSS-Msg</th>
            <th className="px-3 py-2">Teams-Msg</th>
            <th className="px-3 py-2">Fehler / Hinweis</th>
          </tr>
        </thead>
        <tbody>
          {messages.map((m) => (
            <tr key={m.id} className="border-t border-border align-top">
              <td className="px-3 py-2">{m.origin}</td>
              <td className="px-3 py-2">{m.direction}</td>
              <td className="px-3 py-2"><StatusBadge status={m.status} /></td>
              <td className="px-3 py-2 font-mono">{m.tanssMessageId ?? '–'}</td>
              <td className="px-3 py-2 font-mono">{(m.teamsMessageId ?? '–').toString().slice(0, 14)}</td>
              <td className="px-3 py-2 text-danger break-words">{m.lastError ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AuditTable({ entries }: { entries: AuditEntry[] }) {
  // Neueste zuerst
  const sorted = [...entries].sort((a, b) =>
    (b.created_at ?? '').localeCompare(a.created_at ?? ''),
  )
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-surface-alt text-left">
          <tr>
            <th className="px-3 py-2">Zeit</th>
            <th className="px-3 py-2">Aktion</th>
            <th className="px-3 py-2">Richtung</th>
            <th className="px-3 py-2">Note / Fehler</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((a) => (
            <tr key={a.id} className="border-t border-border align-top">
              <td className="px-3 py-2 whitespace-nowrap">{shortTime(a.created_at)}</td>
              <td className="px-3 py-2 font-medium">{a.action}</td>
              <td className="px-3 py-2">{a.direction}</td>
              <td className="px-3 py-2 break-words">
                {a.error ? (
                  <span className="text-danger block">{a.error}</span>
                ) : null}
                {a.note ? (
                  <span className={a.error ? 'text-muted block mt-1' : 'block'}>{a.note}</span>
                ) : null}
                {!a.error && !a.note ? '–' : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function shortTime(iso: string | null | undefined): string {
  if (!iso) return '–'
  // YYYY-MM-DDTHH:MM:SS → DD.MM. HH:MM:SS
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}:\d{2}:\d{2})/.exec(iso)
  if (!m) return iso
  return `${m[3]}.${m[2]}. ${m[4]}`
}

// ─── Tab: Nachrichten ───────────────────────────────────────────────────────

function MessagesTab() {
  const { toast } = useToast()
  const [items, setItems] = useState<MessageSync[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    setError(null)
    try {
      setItems(await listMessageSyncs({ limit: 200 }))
    } catch (e) {
      setError(extractErrorDetails(e))
    }
  }
  useEffect(() => { void load() }, [])

  async function onRetry(id: string) {
    try {
      await retryMessageSync(id)
      toast({ tone: 'success', title: 'Retry ausgeloest.' })
      void load()
    } catch (e) {
      toast({ tone: 'danger', title: extractErrorDetails(e) })
    }
  }

  if (error) return <ErrorState title="Nachrichten konnten nicht geladen werden." details={error} />
  if (!items) return <LoadingState />
  if (items.length === 0) return <EmptyState title="Keine Nachrichten-Syncs" description="Wird mit aktivem Polling-Job gefuellt." />

  return (
    <DataTable<MessageSync>
      data={items}
      getRowId={(r) => r.id}
      columns={[
        { accessorKey: 'origin', header: 'Origin' },
        { accessorKey: 'direction', header: 'Direction' },
        {
          accessorKey: 'status',
          header: 'Status',
          cell: ({ row }) => <StatusBadge status={row.original.status} />,
        },
        { accessorKey: 'tanssMessageId', header: 'TANSS-Msg' },
        {
          accessorKey: 'teamsMessageId',
          header: 'Teams-Msg',
          cell: ({ row }) =>
            row.original.teamsMessageId ? row.original.teamsMessageId.slice(0, 16) + '…' : '–',
        },
        {
          accessorKey: 'lastError',
          header: 'Fehler',
          cell: ({ row }) =>
            row.original.lastError ? (
              <span className="text-xs text-danger" title={row.original.lastError}>
                {row.original.lastError.slice(0, 60)}
                {row.original.lastError.length > 60 ? '…' : ''}
              </span>
            ) : (
              '–'
            ),
        },
        {
          id: 'actions',
          header: 'Aktion',
          enableSorting: false,
          cell: ({ row }) => (
            <Button
              variant="secondary"
              size="xs"
              onClick={() => void onRetry(row.original.id)}
              disabled={['synced', 'ignored', 'needs_manual_action'].includes(row.original.status)}
            >
              Retry
            </Button>
          ),
        },
      ]}
    />
  )
}

// ─── Tab: User-Mapping ──────────────────────────────────────────────────────

function UserMappingsTab() {
  const { toast } = useToast()
  const [items, setItems] = useState<UserMapping[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    setError(null)
    try {
      setItems(await listUserMappings({ limit: 500 }))
    } catch (e) {
      setError(extractErrorDetails(e))
    }
  }
  useEffect(() => { void load() }, [])

  async function onRefresh(tanssUserId: string) {
    try {
      await refreshUserMapping(tanssUserId)
      toast({ tone: 'success', title: 'Mapping aktualisiert.' })
      void load()
    } catch (e) {
      toast({ tone: 'danger', title: extractErrorDetails(e) })
    }
  }

  if (error) return <ErrorState title="User-Mappings konnten nicht geladen werden." details={error} />
  if (!items) return <LoadingState />
  if (items.length === 0) {
    return <EmptyState title="Noch keine User-Mappings" description="Mappings werden beim ersten Erstellen eines Teams-Chats fuer TANSS-Teilnehmer angelegt." />
  }

  return (
    <DataTable<UserMapping>
      data={items}
      getRowId={(r) => r.id}
      columns={[
        {
          accessorKey: 'tanssUserId',
          header: 'TANSS-ID',
          cell: ({ row }) => (
            <span className="font-mono text-xs">{row.original.tanssUserId}</span>
          ),
        },
        {
          accessorKey: 'tanssShortName',
          header: 'TANSS-Kuerzel',
          cell: ({ row }) => row.original.tanssShortName ?? '–',
        },
        { accessorKey: 'tanssDisplayName', header: 'TANSS-Name' },
        { accessorKey: 'teamsEmail', header: 'Teams-Mail' },
        { accessorKey: 'teamsDisplayName', header: 'Teams-Name' },
        {
          accessorKey: 'status',
          header: 'Status',
          cell: ({ row }) => <StatusBadge status={row.original.status} />,
        },
        {
          accessorKey: 'lastVerifiedAt',
          header: 'Geprueft',
          cell: ({ row }) =>
            row.original.lastVerifiedAt
              ? new Date(row.original.lastVerifiedAt).toLocaleString('de-DE')
              : '–',
        },
        {
          accessorKey: 'lastError',
          header: 'Fehler',
          cell: ({ row }) =>
            row.original.lastError ? (
              <span className="text-xs text-danger" title={row.original.lastError}>
                {row.original.lastError.slice(0, 60)}
                {row.original.lastError.length > 60 ? '…' : ''}
              </span>
            ) : (
              '–'
            ),
        },
        {
          id: 'actions',
          header: 'Aktion',
          enableSorting: false,
          cell: ({ row }) => (
            <Button
              variant="secondary"
              size="xs"
              onClick={() => void onRefresh(row.original.tanssUserId)}
            >
              Aktualisieren
            </Button>
          ),
        },
      ]}
    />
  )
}

// ─── Tab: Subscriptions ─────────────────────────────────────────────────────

function SubscriptionsTab() {
  const [items, setItems] = useState<BridgeSubscription[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void listSubscriptions()
      .then((s) => !cancelled && setItems(s))
      .catch((e: unknown) => !cancelled && setError(extractErrorDetails(e)))
    return () => {
      cancelled = true
    }
  }, [])

  if (error) return <ErrorState title="Subscriptions konnten nicht geladen werden." details={error} />
  if (!items) return <LoadingState />
  if (items.length === 0) return <EmptyState title="Keine Subscriptions" description="Wird automatisch beim ersten Teams-Chat angelegt." />

  return (
    <DataTable<BridgeSubscription>
      data={items}
      getRowId={(r) => r.id}
      columns={[
        { accessorKey: 'tanss_ticket_id', header: 'Ticket', cell: ({ row }) => `#${row.original.tanss_ticket_id ?? '–'}` },
        { accessorKey: 'subscription_id', header: 'Subscription-ID' },
        {
          accessorKey: 'expires_at',
          header: 'Laeuft ab',
          cell: ({ row }) => new Date(row.original.expires_at).toLocaleString('de-DE'),
        },
        {
          accessorKey: 'status',
          header: 'Status',
          cell: ({ row }) => <StatusBadge status={row.original.status} />,
        },
        {
          accessorKey: 'last_renewal_at',
          header: 'Letzter Renew',
          cell: ({ row }) =>
            row.original.last_renewal_at
              ? new Date(row.original.last_renewal_at).toLocaleString('de-DE')
              : '–',
        },
      ]}
    />
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function extractErrorDetails(err: unknown): string {
  if (!err) return 'Unbekannter Fehler'
  if (typeof err === 'string') return err
  const anyErr = err as {
    response?: { data?: { error?: string; message?: string } }
    message?: string
  }
  return (
    anyErr.response?.data?.error ??
    anyErr.response?.data?.message ??
    anyErr.message ??
    JSON.stringify(err)
  )
}

// ─── Debug: Abteilung ────────────────────────────────────────────────────────

function DebugDeptTab() {
  const [deptId, setDeptId] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<DeptDebugResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function run() {
    const id = Number(deptId)
    if (!id) return
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      setResult(await debugDept(id))
    } catch (e) {
      setError(extractErrorDetails(e))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-body">
        Gibt alle TANSS-Mitglieder einer Abteilung aus und zeigt wo die Auflösung zu Teams scheitert
        (fehlende E-Mail, kein SysMan-User mit TANSS-ID, kein Entra-ID).
      </p>
      <div className="flex gap-2 items-end">
        <div>
          <label className="text-xs text-muted block mb-1">Abteilungs-ID (TANSS)</label>
          <input
            type="number"
            value={deptId}
            onChange={(e) => setDeptId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void run()}
            placeholder="z.B. 61"
            className="border rounded px-2 py-1 text-sm w-32 font-mono"
          />
        </div>
        <Button variant="primary" size="sm" onClick={() => void run()} disabled={loading || !deptId}>
          {loading ? 'Lädt…' : 'Testen'}
        </Button>
      </div>

      {error && <ErrorState title="Fehler beim Debug-Test" details={error} />}

      {result && (
        <div className="space-y-3">
          {result.deptError && (
            <ErrorState title={`getDepartmentMembers(${result.departmentId}) fehlgeschlagen`} details={result.deptError} />
          )}
          <p className="text-sm">
            Abteilung <strong>{result.departmentId}</strong>: <strong>{result.memberCount}</strong> Mitglieder
            {' '}({result.members.filter(m => m.canSync).length} können synchronisiert werden)
          </p>
          <div className="space-y-2">
            {result.members.map((m) => (
              <div
                key={m.tanssId}
                className={`border rounded p-3 text-sm space-y-1 ${m.canSync ? 'border-success/40 bg-success/5' : 'border-danger/40 bg-danger/5'}`}
              >
                <div className="flex items-center gap-2 font-medium">
                  <span className={m.canSync ? 'text-success' : 'text-danger'}>{m.canSync ? '✓' : '✗'}</span>
                  <span>{m.name}</span>
                  <span className="font-mono text-xs text-muted">TANSS-ID: {m.tanssId}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-muted pl-5">
                  <span>TANSS-E-Mail: {m.tanssEmail ?? (m.emailError ? <span className="text-danger">{m.emailError}</span> : <span className="text-warning">nicht gefunden</span>)}</span>
                  <span>SysMan-User: {m.sysmanUser ? `${m.sysmanUser.name} (${m.sysmanUser.email})` : <span className="text-danger">kein User mit dieser TANSS-ID</span>}</span>
                  <span>Entra-ID (SysMan): {m.sysmanUser?.entraId ?? <span className="text-warning">nicht gesetzt</span>}</span>
                  <span>Mapping-Status: {m.mapping ? <StatusBadge status={m.mapping.status} /> : <span className="opacity-50">–</span>}</span>
                  {m.mapping?.lastError && <span className="col-span-2 text-danger">Mapping-Fehler: {m.mapping.lastError}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Debug: User ─────────────────────────────────────────────────────────────

function DebugUsersTab() {
  const [items, setItems] = useState<DebugUser[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    setError(null)
    try {
      setItems(await listDebugUsers())
    } catch (e) {
      setError(extractErrorDetails(e))
    }
  }
  useEffect(() => { void load() }, [])

  if (error) return <ErrorState title="User-Liste konnte nicht geladen werden." details={error} />
  if (!items) return <LoadingState />

  const ok = (v: boolean) => v ? '✓' : '✗'

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <p className="text-sm text-body flex-1">
          {items.length} interne SysMan-User — zeigt Name, E-Mail, TANSS-ID, Entra-ID und Mapping-Status.
        </p>
        <Button variant="secondary" size="sm" onClick={() => void load()}>Aktualisieren</Button>
      </div>
      <DataTable<DebugUser>
        data={items}
        getRowId={(r) => r.id}
        columns={[
          {
            accessorKey: 'name',
            header: 'Name',
            cell: ({ row }) => (
              <span className={row.original.isActive ? '' : 'opacity-40 line-through'}>
                {row.original.name}
              </span>
            ),
          },
          { accessorKey: 'email', header: 'E-Mail' },
          {
            accessorKey: 'tanssId',
            header: 'TANSS-ID',
            cell: ({ row }) => row.original.tanssId
              ? <span className="font-mono text-xs">{row.original.tanssId}</span>
              : <span className="text-xs text-danger">nicht gesetzt</span>,
          },
          {
            accessorKey: 'tanssShortName',
            header: 'TANSS-Kürzel',
            cell: ({ row }) => row.original.tanssShortName ?? row.original.tanssDisplayName ?? '–',
          },
          {
            accessorKey: 'entraId',
            header: 'Entra-ID',
            cell: ({ row }) => row.original.entraId
              ? <span className="font-mono text-xs">{row.original.entraId}</span>
              : <span className="text-xs text-warning">nicht gesetzt</span>,
          },
          {
            accessorKey: 'hasM365Token',
            header: 'M365',
            cell: ({ row }) => (
              <span className={row.original.hasM365Token ? 'text-success' : 'text-danger'}>
                {ok(row.original.hasM365Token)} Token
              </span>
            ),
          },
          {
            accessorKey: 'mappingStatus',
            header: 'Mapping',
            cell: ({ row }) => row.original.mappingStatus
              ? <StatusBadge status={row.original.mappingStatus} />
              : <span className="text-xs text-body opacity-50">–</span>,
          },
          {
            accessorKey: 'mappingLastError',
            header: 'Letzter Fehler',
            cell: ({ row }) => row.original.mappingLastError
              ? <span className="text-xs text-danger" title={row.original.mappingLastError}>
                  {row.original.mappingLastError.slice(0, 60)}{row.original.mappingLastError.length > 60 ? '…' : ''}
                </span>
              : <span className="text-xs opacity-40">–</span>,
          },
        ]}
      />
    </div>
  )
}

// ─── Tab: Chat-Diagnose ────────────────────────────────────────────────────
//
// Eingabe einer TANSS-chatId → der Endpunkt liefert:
//  - was die Bridge lokal ueber den Chat weiss (ticket_sync, audit)
//  - wer den Chat laut TANSS geschlossen hat (closedByEmployeeId + Name)
//  - wer die Teams-System-Message(n) im Namen welcher Identitaet gepostet hat
//    (joined ueber users.m365_user_id; bei Alt-Eintraegen ohne Author bleibt
//    die Spalte leer — neue Closes nach Deploy speichern den Author).

function DiagnoseChatTab() {
  const [chatId, setChatId] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<DiagnoseChatResult | null>(null)

  async function run() {
    const id = chatId.trim()
    if (!id) return
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      setResult(await diagnoseChat(id))
    } catch (e) {
      setError(extractErrorDetails(e))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <Card.Body>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => { e.preventDefault(); void run() }}
          >
            <div className="flex-1 min-w-[200px]">
              <Input
                label="TANSS-Chat-ID"
                hint="Die numerische TANSS-chatId (z.B. 22251), nicht die Teams-Thread-ID."
                value={chatId}
                onChange={(e) => setChatId(e.target.value)}
                placeholder="22251"
              />
            </div>
            <Button type="submit" variant="primary" disabled={loading || !chatId.trim()}>
              {loading ? 'Suche…' : 'Diagnose laden'}
            </Button>
          </form>
        </Card.Body>
      </Card>

      {error && <ErrorState title="Diagnose fehlgeschlagen." details={error} />}
      {result && <DiagnoseChatResultView result={result} />}
    </div>
  )
}

function DiagnoseChatResultView({ result }: { result: DiagnoseChatResult }) {
  return (
    <div className="space-y-4">
      <Card>
        <Card.Header><h3 className="text-h4">TANSS Live ({result.tanssChatId})</h3></Card.Header>
        <Card.Body>
          {result.tanssLive.reachable ? (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-muted">Geschlossen von</dt>
              <dd>
                {result.tanssLive.closedByEmployeeId ? (
                  <>
                    <span className="font-medium">{result.tanssLive.closedByName ?? '(Name unbekannt)'}</span>
                    <span className="font-mono text-xs text-muted ml-2">#{result.tanssLive.closedByEmployeeId}</span>
                  </>
                ) : (
                  <span className="text-muted">Chat ist nicht geschlossen / TANSS liefert keinen closedBy</span>
                )}
              </dd>
              <dt className="text-muted">Angelegt von</dt>
              <dd>
                {result.tanssLive.createdByEmployeeId ? (
                  <>
                    <span>{result.tanssLive.createdByName ?? '(Name unbekannt)'}</span>
                    <span className="font-mono text-xs text-muted ml-2">#{result.tanssLive.createdByEmployeeId}</span>
                  </>
                ) : '–'}
              </dd>
              <dt className="text-muted">Ticket-ID</dt>
              <dd className="font-mono text-xs">{result.tanssLive.ticketId ?? '–'}</dd>
            </dl>
          ) : (
            <ErrorState title="TANSS nicht erreichbar oder Chat unbekannt" details={result.tanssLive.error ?? 'Unbekannter Fehler'} />
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <h3 className="text-h4">Teams-System-Messages ({result.systemMessages.length})</h3>
        </Card.Header>
        <Card.Body className="p-0">
          {result.systemMessages.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted">
              {result.ticketSync
                ? 'Kein SYSTEM-message_sync fuer diesen Chat. (Alt-Eintraege ohne origin=SYSTEM oder es wurde nie geschlossen.)'
                : 'Kein ticket_sync — die Bridge kennt diesen Chat lokal nicht.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-surface-alt text-left">
                  <tr>
                    <th className="px-3 py-2">Zeit</th>
                    <th className="px-3 py-2">Author (Teams)</th>
                    <th className="px-3 py-2">Entra-ID</th>
                    <th className="px-3 py-2">Teams-Msg-ID</th>
                  </tr>
                </thead>
                <tbody>
                  {result.systemMessages.map((m) => (
                    <tr key={m.id} className="border-t border-border align-top">
                      <td className="px-3 py-2 whitespace-nowrap">{shortTime(m.createdAt)}</td>
                      <td className="px-3 py-2">
                        {m.authorName ? (
                          <span className="font-medium">{m.authorName}</span>
                        ) : m.authorEntraId ? (
                          <span className="text-muted">(unbekannter User)</span>
                        ) : (
                          <span className="text-warning">nicht erfasst (alter Eintrag)</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs break-all">{m.authorEntraId ?? '–'}</td>
                      <td className="px-3 py-2 font-mono text-xs break-all">{(m.teamsMessageId ?? '–').toString().slice(0, 20)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <h3 className="text-h4">Close-/Reopen-Audit ({result.auditCloses.length})</h3>
        </Card.Header>
        <Card.Body className="p-0">
          {result.auditCloses.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted">Kein Close-/Reopen-Audit-Eintrag.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-surface-alt text-left">
                  <tr>
                    <th className="px-3 py-2">Zeit</th>
                    <th className="px-3 py-2">Aktion</th>
                    <th className="px-3 py-2">Richtung</th>
                    <th className="px-3 py-2">Note / Fehler</th>
                  </tr>
                </thead>
                <tbody>
                  {result.auditCloses.map((a) => (
                    <tr key={a.id} className="border-t border-border align-top">
                      <td className="px-3 py-2 whitespace-nowrap">{shortTime(a.createdAt)}</td>
                      <td className="px-3 py-2 font-medium">{a.action}</td>
                      <td className="px-3 py-2">{a.direction}</td>
                      <td className="px-3 py-2 break-words">
                        {a.error && <span className="text-danger block">{a.error}</span>}
                        {a.note && <span className={a.error ? 'text-muted block mt-1' : 'block'}>{a.note}</span>}
                        {!a.error && !a.note && '–'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      {!result.ticketSync && (
        <Card>
          <Card.Body>
            <p className="text-sm text-warning">
              Hinweis: Es gibt keinen lokalen <code className="font-mono text-xs">ticket_sync</code>-Eintrag fuer diese
              TANSS-Chat-ID. Die Bridge hat den Chat entweder noch nie gesehen, oder die ID stimmt nicht.
            </p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}
